import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';
import type { BillingAlert } from '../services/api.ts';
import { getSafeHttpsUrl } from '../utils/safeUrl.ts';

interface BillingTopBannerProps {
  alert: BillingAlert | null;
}

const formatDate = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const formatPrice = (value?: number | null): string | null => {
  if (value == null) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const BillingTopBanner: React.FC<BillingTopBannerProps> = ({ alert }) => {
  const { isDark } = useTheme();

  if (!alert) return null;

  const isDanger = alert.severity === 'danger' || alert.type === 'overdue';
  const dueDate = formatDate(alert.due_date || alert.payment?.due_date);
  const value = formatPrice(alert.value ?? alert.payment?.value);
  const paymentUrl = getSafeHttpsUrl(
    alert.payment_url || alert.payment?.payment_url || alert.payment?.invoice_url || alert.payment?.bank_slip_url,
  );
  const details = [dueDate ? `Vencimento ${dueDate}` : null, value].filter(Boolean).join(' - ');

  return (
    <section
      className={`border-b ${
        isDanger
          ? isDark
            ? 'border-red-900/60 bg-red-950/70 text-red-100'
            : 'border-red-200 bg-red-50 text-red-900'
          : isDark
            ? 'border-amber-900/60 bg-amber-950/70 text-amber-100'
            : 'border-amber-200 bg-amber-50 text-amber-950'
      }`}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <div
            className={`mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg ${
              isDanger
                ? isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'
                : isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{alert.title}</p>
            <p className="mt-0.5 text-sm opacity-90">
              {alert.message}
            </p>
            {details && (
              <p className="mt-1 text-xs font-medium opacity-75">
                {details}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          {paymentUrl && (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors ${
                isDanger
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              Pagar agora
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <Link
            to="/reports"
            className={`inline-flex h-9 items-center justify-center rounded-lg border px-3 text-sm font-medium transition-colors ${
              isDanger
                ? isDark
                  ? 'border-red-700/70 text-red-100 hover:bg-red-900/50'
                  : 'border-red-200 bg-white/70 text-red-800 hover:bg-white'
                : isDark
                  ? 'border-amber-700/70 text-amber-100 hover:bg-amber-900/50'
                  : 'border-amber-200 bg-white/70 text-amber-900 hover:bg-white'
            }`}
          >
            Ver faturas
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BillingTopBanner;
